import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEOOptimization } from './hooks/useSEOOptimization';
import { useGoogleAnalytics, usePagePerformance } from './hooks/useGoogleAnalytics';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './services/axiosInterceptor';
import Navbar from './components/layout/Navbar/Navbar';
import Footer from './components/layout/Footer/Footer';
import InfoBanner from './components/common/InfoBanner/InfoBanner';
import SessionManager from './components/auth/SessionManager';
import UpdateNotification from './components/UpdateNotification';
import { AuthProvider } from './contexts/AuthContext';
import CookieConsent from './components/common/CookieConsent/CookieConsent';
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';
import LoadingFallback from './components/LoadingFallback';

// Debug Dashboard (développement seulement)
import AnalyticsDashboard from './components/dev/AnalyticsDashboard';

// Import des composants de page avec lazy loading pour optimiser les performances
// Seules les pages critiques (Home, Login) sont chargées immédiatement
import Home from './pages/Home/Home';
import Login from './pages/Login/Login';

// Lazy loading de toutes les autres pages
const Pricing = React.lazy(() => import('./pages/Pricing/Pricing'));
const FAQ = React.lazy(() => import('./pages/FAQ/FAQ'));
const BlogList = React.lazy(() => import('./pages/Blog/BlogList'));
const BlogPost = React.lazy(() => import('./pages/Blog/BlogPost'));
const Documentation = React.lazy(() => import('./pages/Documentation/Documentation'));
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const Statistics = React.lazy(() => import('./pages/Statistics/Statistics'));
const ActionHistory = React.lazy(() => import('./pages/ActionHistory/ActionHistory'));
const Tags = React.lazy(() => import('./pages/Tags/Tags'));
const BillingHistory = React.lazy(() => import('./pages/Billing/BillingHistory'));
const PrestashopDocs = React.lazy(() => import('./pages/Documentation/PrestashopDocs/PrestashopDocs'));
const RegisterDocs = React.lazy(() => import('./pages/Documentation/RegisterDocs/RegisterDocs'));
const PrincipleDocs = React.lazy(() => import('./pages/Documentation/PrincipleDocs/PrincipleDocs'));
const ChromeDocs = React.lazy(() => import('./pages/Documentation/ChromeDocs/ChromeDocs'));
const ConnectionDocs = React.lazy(() => import('./pages/Documentation/ChromeDocs/ConnectionDocs'));
const ArticleChoiceDocs = React.lazy(() => import('./pages/Documentation/ChromeDocs/ArticleChoiceDocs'));
const ImageEditorDocs = React.lazy(() => import('./pages/Documentation/ChromeDocs/ImageEditorDocs'));

// Import des composants d'affiliation documentation
const AffiliateProgram = React.lazy(() => import('./pages/Documentation/Affiliate/AffiliateProgram'));
const AffiliateQuickStart = React.lazy(() => import('./pages/Documentation/Affiliate/AffiliateQuickStart'));
const AffiliateTerms = React.lazy(() => import('./pages/Documentation/Affiliate/AffiliateTerms'));

// Pages légales
const CGU = React.lazy(() => import('./pages/Legal/CGU'));
const Privacy = React.lazy(() => import('./pages/Legal/Privacy'));
const LegalNotice = React.lazy(() => import('./pages/Legal/LegalNotice'));

// Pages articles
const Articles = React.lazy(() => import('./pages/Articles/Articles'));
const NewArticle = React.lazy(() => import('./pages/Articles/NewArticle'));
const ImportArticles = React.lazy(() => import('./pages/Articles/Import/ImportArticles'));
const ArticleDetail = React.lazy(() => import('./pages/ArticleDetail/ArticleDetail'));

// Pages auth et profil
const Register = React.lazy(() => import('./pages/Register/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail/VerifyEmail'));
const Support = React.lazy(() => import('./pages/Support'));
const Profile = React.lazy(() => import('./pages/Profile/Profile'));

// Pages paiement
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = React.lazy(() => import('./pages/PaymentCancel'));
const AccountSitesManagement = React.lazy(() => import('./pages/Profile/AccountSitesManagement'));
const Subscription = React.lazy(() => import('./pages/Subscription/Subscription'));
const SubscriptionManagementPage = React.lazy(() => import('./pages/SubscriptionManagementPage'));
const PaidOptions = React.lazy(() => import('./pages/PaidOptions/PaidOptions'));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));

// Autres pages
const MesMasques = React.lazy(() => import('./pages/MesMasques'));
const NotFound = React.lazy(() => import('./pages/NotFound/NotFound'));

// Components utilisés globalement (pas de lazy loading)
import GlobalAccountAlert from './components/GlobalAccountAlert';
import ExtensionBanner from './components/Onboarding/ExtensionBanner';

// Import des composants d'administration
const AdminDashboard = React.lazy(() => import('./pages/Admin/AdminDashboard'));
const ReferralManagement = React.lazy(() => import('./pages/Admin/ReferralManagement'));
const RewardManagement = React.lazy(() => import('./pages/Admin/RewardManagement'));
const AffiliateAnalytics = React.lazy(() => import('./pages/Admin/AffiliateAnalytics'));
const AffiliateCodeManagement = React.lazy(() => import('./pages/Admin/AffiliateCodeManagement'));
const PromoCodeManagement = React.lazy(() => import('./pages/Admin/PromoCodeManagement'));
const CategoryMapping = React.lazy(() => import('./pages/Admin/CategoryMapping'));
const CategoryManager = React.lazy(() => import('./pages/Admin/CategoryManager'));
const BrandMapping = React.lazy(() => import('./pages/Admin/BrandMapping'));
const AdminSettings = React.lazy(() => import('./pages/Admin/AdminSettings'));
const AdminDemarchage = React.lazy(() => import('./pages/Admin/AdminDemarchage'));
const StatistiqueReusses = React.lazy(() => import('./pages/Admin/StatistiqueReusses'));
const StatistiqueAcquisition = React.lazy(() => import('./pages/Admin/StatistiqueAcquisition'));

// Import des composants d'affiliation utilisateur
const UserAffiliateDashboard = React.lazy(() => import('./pages/Affiliate/UserAffiliateDashboard'));

// Import des composants de planification
const SchedulingRoutes = React.lazy(() => import('./routes/SchedulingRoutes'));

const App = () => {
  const { t } = useTranslation();
    // Hooks pour Google Analytics
  useGoogleAnalytics(); // Tracking automatique des pages
  usePagePerformance(); // Mesure des performances
  
  // SEO Optimization hook
  useSEOOptimization(); // Optimisations SEO globales
  
  return (
    <AuthProvider>
        <div className="app">
        {/* Notification de mise à jour */}
        <UpdateNotification />
        
        {/* Toast notifications */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        
        {/* Skip to content pour l'accessibilité */}
        <a href="#main-content" className="skip-to-content">
          Aller au contenu principal
        </a>
        
        <SessionManager />
        <CookieConsent />
        <Navbar />
        <GlobalAccountAlert />
        <ExtensionBanner />
        <main id="main-content">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/documentation" element={<Documentation />} />
            
            {/* Routes de documentation avec layout */}
            <Route path="/docs/principle-overview" element={<Documentation />} />
            <Route path="/docs/register" element={<Documentation />} />
            
            {/* Routes des fonctionnalités */}
            <Route path="/docs/features/articles-centralization" element={<Documentation />} />
            <Route path="/docs/features/import-system" element={<Documentation />} />
            <Route path="/docs/features/ai-assistant" element={<Documentation />} />
            <Route path="/docs/features/publish-listings" element={<Documentation />} />
            <Route path="/docs/features/view-exchange" element={<Documentation />} />
            <Route path="/docs/features/favorites-exchange" element={<Documentation />} />
            <Route path="/docs/features/image-editor" element={<Documentation />} />
            <Route path="/docs/features/auto-messages" element={<Documentation />} />
            <Route path="/docs/features/automatic-deletion" element={<Documentation />} />
            <Route path="/docs/features/sales-dashboard" element={<Documentation />} />
            
            {/* Routes de l'extension Chrome */}
            <Route path="/docs/chrome-installation" element={<Documentation />} />
            <Route path="/docs/chrome-connection" element={<Documentation />} />
            <Route path="/docs/chrome-article-choice" element={<Documentation />} />
            <Route path="/docs/chrome-publication" element={<Documentation />} />
            <Route path="/docs/chrome-image-editor" element={<Documentation />} />
            <Route path="/docs/chrome-views-favoris" element={<Documentation />} />
            <Route path="/docs/chrome-messages-offres" element={<Documentation />} />
            <Route path="/docs/chrome-suppression" element={<Documentation />} />
            <Route path="/docs/chrome-catalogue" element={<Documentation />} />
            
            {/* Routes des sites */}
            <Route path="/docs/sales-config" element={<Documentation />} />
            <Route path="/docs/article-management" element={<Documentation />} />
            <Route path="/docs/automation" element={<Documentation />} />
            
            {/* Routes des plateformes */}
            <Route path="/docs/prestashop" element={<Documentation />} />
            <Route path="/docs/woocommerce" element={<Documentation />} />
            <Route path="/docs/shopify" element={<Documentation />} />

            {/* Routes des partenariats */}
            <Route path="/docs/partnership-reusses" element={<Documentation />} />
              {/* Routes d'affiliation avec layout documentation */}
            <Route path="/affiliate/program" element={<Documentation />} />
            <Route path="/affiliate/quick-start" element={<Documentation />} />
            <Route path="/affiliate/terms" element={<Documentation />} />
            
            {/* FAQ : 12 questions avec balisage Schema.org FAQPage, très cité
                par les moteurs IA. La page existait déjà mais n'était pas routée. */}
            <Route path="/faq" element={<FAQ />} />
            {/* Lien court des SMS de prospection, attribué dans services/attribution.js (SHORT_LINKS) */}
            <Route path="/sms" element={<Navigate to="/?utm_source=brevo&utm_medium=sms&utm_campaign=prospection-sms" replace />} />

            {/* Blog : articles en Markdown (src/content/blog), prérendus au build */}
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            <Route path="/cgu" element={<CGU />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/mentions-legales" element={<LegalNotice />} />
            <Route path="/articles" element={
              <PrivateRoute>
                <Articles />
              </PrivateRoute>
            } />
            <Route path="/articles/import" element={
              <PrivateRoute>
                <ImportArticles />
              </PrivateRoute>
            } />
            <Route path="/articles/import-history" element={
              <PrivateRoute>
                <Articles showImportHistory={true} />
              </PrivateRoute>
            } />
            <Route path="/articles/new" element={
              <PrivateRoute>
                <NewArticle />
              </PrivateRoute>
            } />
            <Route path="/articles/edit/:id" element={
              <PrivateRoute>
                <NewArticle />
              </PrivateRoute>
            } />
            <Route path="/articles/:id" element={
              <PrivateRoute>
                <ArticleDetail />
              </PrivateRoute>
            } />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/support" element={<Support />} />
            <Route path="/subscription" element={
              <PrivateRoute bypassPaymentCheck={true}>
                <Subscription />
              </PrivateRoute>
            } />
            <Route path="/subscription-management" element={
              <PrivateRoute bypassPaymentCheck={true}>
                <SubscriptionManagementPage />
              </PrivateRoute>
            } />
            <Route path="/profile" element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            } />
            <Route path="/account-sites" element={
              <PrivateRoute>
                <AccountSitesManagement />
              </PrivateRoute>
            } />

            <Route path="/billing-history" element={
              <PrivateRoute>
                <Suspense fallback={<div>{t('common.loading')}</div>}>
                  <BillingHistory />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/paid-options" element={
              <PrivateRoute>
                <PaidOptions />
              </PrivateRoute>
            } />
            <Route path="/orders" element={
              <PrivateRoute>
                <OrdersPage />
              </PrivateRoute>
            } />
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/statistics" element={
              <PrivateRoute>
                <Statistics />
              </PrivateRoute>
            } />
            <Route path="/tags" element={
              <PrivateRoute>
                <Tags />
              </PrivateRoute>
            } />

            {/* Route Mes Masques */}
            <Route path="/mes-masques" element={
              <PrivateRoute>
                <MesMasques />
              </PrivateRoute>
            } />

            {/* Historique des actions de l'utilisateur */}
            <Route path="/actions" element={
              <PrivateRoute>
                <ActionHistory />
              </PrivateRoute>
            } />

            {/* Routes de planification */}
            <Route path="/scheduling/*" element={
              <PrivateRoute>
                <SchedulingRoutes />
              </PrivateRoute>
            } />

            {/* Routes d'affiliation utilisateur */}
            <Route path="/affiliate" element={
              <PrivateRoute>
                <UserAffiliateDashboard />
              </PrivateRoute>
            } />
            <Route path="/affiliate/referrals" element={
              <PrivateRoute>
                <UserAffiliateDashboard activeTab="referrals" />
              </PrivateRoute>
            } />
            <Route path="/affiliate/rewards" element={
              <PrivateRoute>
                <UserAffiliateDashboard activeTab="rewards" />
              </PrivateRoute>
            } />
            <Route path="/affiliate/code" element={
              <PrivateRoute>
                <UserAffiliateDashboard activeTab="code" />
              </PrivateRoute>
            } />
            
            {/* Routes d'administration */}
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/referrals" element={
              <AdminRoute>
                <ReferralManagement />
              </AdminRoute>
            } />
            <Route path="/admin/rewards" element={
              <AdminRoute>
                <RewardManagement />
              </AdminRoute>
            } />            <Route path="/admin/analytics" element={
              <AdminRoute>
                <AffiliateAnalytics />
              </AdminRoute>
            } />
            <Route path="/admin/codes" element={
              <AdminRoute>
                <AffiliateCodeManagement />
              </AdminRoute>
            } />
            <Route path="/admin/promo-codes" element={
              <AdminRoute>
                <PromoCodeManagement />
              </AdminRoute>
            } />
            <Route path="/admin/category-mapping" element={
              <AdminRoute>
                <CategoryMapping />
              </AdminRoute>
            } />
            <Route path="/admin/brand-mapping" element={
              <AdminRoute>
                <BrandMapping />
              </AdminRoute>
            } />
            <Route path="/admin/categories" element={
              <AdminRoute>
                <CategoryManager />
              </AdminRoute>
            } />
            <Route path="/admin/settings" element={
              <AdminRoute>
                <AdminSettings />
              </AdminRoute>
            } />
            <Route path="/admin/demarchage" element={
              <AdminRoute>
                <AdminDemarchage />
              </AdminRoute>
            } />
            <Route path="/admin/acquisition" element={
              <AdminRoute>
                <StatistiqueAcquisition />
              </AdminRoute>
            } />
            <Route path="/admin/stats-reusses" element={
              <AdminRoute requiredRoles={['ROLE_ADMIN_REUSSES']}>
                <StatistiqueReusses />
              </AdminRoute>
            } />

            {/* Route 404 - doit être en dernier */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </main>
        
        {/* Bandeau d'information en bas de page */}
        <InfoBanner />
        
        <Footer />
        
        {/* Dashboard Analytics pour le développement */}
        <AnalyticsDashboard />
        </div>
    </AuthProvider>
  );
};

export default App;
